<template>
  <div style="position: relative">
    <div class="d-flex justify-space-between align-center">
      <h4 class="mb-0">
        Solicitação de Recurso:
        <span class="font-weight-medium">
          <span v-if="resourceRequest?.code"> {{ resourceRequest?.code }} </span>
          <span v-else> #{{ id | shortId(5) }} </span>
        </span>
      </h4>
      <v-chip
        v-if="resourceRequest"
        label
        :color="statuses[resourceRequest.status].color"
        class="white--text"
      >
        {{ statuses[resourceRequest.status].text }}
      </v-chip>
    </div>

    <v-alert v-if="error && itens === false" type="error" class="mt-4">
      {{ error }}
    </v-alert>
    <div
      v-if="loading && itens === false"
      class="d-flex flex-column align-center justify-center pt-16"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
        width="6"
        class="mb-4"
      />
      <h6>Carregando solicitação de recurso...</h6>
    </div>

    <v-row v-if="itens !== false">
      <v-col cols="12" md="4" order-md="1">
        <v-card
          v-if="party"
          rounded="lg"
          class="pa-3 mb-4 d-flex flex-column gap-3"
        >
          <div class="d-flex gap-4 align-center">
            <div>
              <v-img
                :src="party.cover"
                width="120px"
                class="rounded-lg"
                :aspect-ratio="16 / 9"
              />
            </div>
            <div class="flex-grow-1">
              <span class="d-block mb-0 text-overline lh-1">
                {{ party.date | date }} - {{ party.endDate | date }}
              </span>
              <h6 class="mb-0">{{ party.name }}</h6>
              <span class="d-block" v-if="party.Address">
                {{ party.Address.city }}/{{ party.Address.state }}
              </span>
            </div>
          </div>

          <div v-if="resourceRequest?.Owner">
            <v-divider class="mb-2" />
            <div class="d-flex gap-4 align-center">
              <div>
                <v-icon v-if="!resourceRequest?.Owner?.name" size="38">
                  mdi-account-question
                </v-icon>
                <base-avatar
                  v-else
                  size="40"
                  :src="resourceRequest?.Owner?.photo"
                  :seed="resourceRequest?.Owner?.id"
                />
              </div>
              <div>
                <h6 class="mb-0">
                  {{ resourceRequest?.Owner?.name || "Conta não criada" }}
                </h6>
                <span>{{ resourceRequest?.Owner?.phone }}</span>
              </div>
            </div>
          </div>
          <div v-if="resourceRequest?.BankAccount">
            <v-divider class="mb-2" />
            <div class="d-flex gap-4 align-center">
              <v-icon size="38">mdi-bank</v-icon>
              <div>
                <h6 class="mb-0">
                  {{ resourceRequest?.BankAccount?.bank }}
                </h6>
                <span
                  >Chave Pix:
                  <b>{{ resourceRequest?.BankAccount?.pixKey }}</b></span
                >
              </div>
            </div>
          </div>
          <div v-if="resourceRequest?.message">
            <v-divider class="mb-2" />
            <div class="d-flex gap-4 align-start">
              <v-icon size="38">mdi-message</v-icon>
              <div>
                <b class="d-block">Mensagem:</b>
                <span class="d-block">{{ resourceRequest?.message }}</span>
              </div>
            </div>
          </div>
          <div v-if="resourceRequest?.rejectReason">
            <v-divider class="mb-2" />
            <div class="d-flex gap-4 align-start">
              <v-icon size="38">mdi-close</v-icon>
              <div>
                <b class="d-block">Motivo da rejeição:</b>
                <span class="d-block">{{ resourceRequest?.rejectReason }}</span>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="8" order-md="0">
        <v-expansion-panels v-model="activePanel" focusable>
          <v-expansion-panel v-for="(item, key) in itens" :key="key">
            <v-expansion-panel-header>
              <div class="flex-grow-0">
                <v-icon left>{{ types[item.type].icon }}</v-icon>
                <b>{{ types[item.type].name }}</b>
              </div>
              <v-spacer />
              <div class="flex-grow-0">
                <span v-if="item.valid">
                  <b>{{ item.amount | currency(true) }}</b>
                  <v-icon color="green" left right>mdi-check</v-icon>
                </span>
                <span v-else>
                  <v-icon color="red" left right>mdi-close</v-icon>
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="pt-3" eager>
              <component
                :is="types[item.type].component"
                :value="item.data"
                :readonly="resourceRequest?.status !== 'DRAFT' || !!loading"
                @update="updateItem(key, $event)"
                @update:valid="updateValid(key, $event)"
                @update:amount="updateTotal(key, $event)"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-menu v-if="resourceRequest?.status === 'DRAFT'">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              block
              class="mt-4"
              text
              :disabled="!!loading"
            >
              Adicionar Item
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="item in types"
              :key="item.id"
              @click="addItem(item.id)"
            >
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.description }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>

        <div class="text-end mt-4">
          <template v-if="resourceRequest?.partyTotalDebit">
            <p class="mb-0">
              Evento: {{ resourceRequest?.partyTotalDebit | currency(true) }}
            </p>
            <p class="mb-0">
              Empresa:
              {{ (total - resourceRequest?.partyTotalDebit) | currency(true) }}
            </p>
          </template>
          <h5 class="mb-0">Total: {{ total | currency(true) }}</h5>
        </div>
      </v-col>
    </v-row>

    <div
      class="d-flex justify-center"
      style="
        position: fixed;
        bottom: 100px;
        left: 24px;
        right: 24px;
        pointer-events: none;
      "
    >
      <v-card
        style="pointer-events: auto; max-width: 700px; width: 100%"
        class="rounded-lg"
        elevation="10"
      >
        <div
          v-if="resourceRequest?.status === 'DRAFT'"
          class="d-flex justify-end align-center gap-4 ma-2"
        >
          <v-btn
            small
            icon
            color="error"
            :disabled="!!loading"
            :loading="loading === 'delete'"
            @click="deleteDraft"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            text
            :disabled="!!loading || !itens.length || successSave"
            :loading="loading === 'draft'"
            @click="saveDraft"
          >
            <v-icon v-if="successSave" left>mdi-check</v-icon>
            {{ successSave ? "Rascunho salvo" : "Salvar rascunho" }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!isValid || !!loading"
            :loading="loading === 'submit'"
            @click="submit"
          >
            Enviar para aprovação
          </v-btn>
        </div>
        <template v-else-if="resourceRequest?.status === 'SUBMITTED'">
          <div
            v-if="hasPermission(8388608)"
            class="d-flex justify-end align-center gap-4 ma-2"
          >
            <v-btn
              text
              color="primary"
              :disabled="!!loading"
              :loading="loading === 'toDraft'"
              @click="toDraft"
            >
              Voltar para rascunho
            </v-btn>
            <v-spacer />
            <v-btn
              color="error"
              :disabled="!!loading"
              :loading="loading === 'reject'"
              @click="reject"
            >
              Rejeitar
            </v-btn>
            <v-btn
              color="success"
              :disabled="!!loading"
              :loading="loading === 'approve'"
              @click="$emit('approve-resource-request')"
            >
              Aprovar
            </v-btn>
          </div>
          <v-alert v-else type="info" class="ma-0" text>
            <b>Solicitação enviada para aprovação</b>
            <span class="d-block">
              A solicitação foi enviada para aprovação.
            </span>
          </v-alert>
        </template>
        <v-alert
          v-else-if="resourceRequest?.status === 'REJECTED'"
          type="error"
          class="ma-0"
          text
          dense
        >
          <b>Solicitação rejeitada</b>
          <span class="d-block">Não é possível alterar ou excluir a solicitação.</span>
        </v-alert>
        <v-alert
          v-else-if="resourceRequest?.status === 'APPROVED'"
          type="success"
          class="ma-0"
          text
          dense
        >
          <b>Solicitação aprovada</b>
          <span class="d-block">Não é possível alterar ou excluir a solicitação.</span>
        </v-alert>
      </v-card>
    </div>

    <v-alert v-if="error" type="error" class="mt-4">
      {{ error }}
    </v-alert>

    <ResourceRequestSubmitModal
      v-if="resourceRequest?.status === 'DRAFT'"
      :types="types"
      :itens="itens"
      :party="party"
      :resourceRequest="resourceRequest"
      @success="loadResourceRequest"
    />
    <ResourceRequestAproveModal
      v-if="resourceRequest?.status === 'SUBMITTED'"
      :types="types"
      :itens="itens"
      :party="party"
      :resourceRequest="resourceRequest"
      @success="loadResourceRequest"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import RESOURCE_REQUEST from "@/services/admin/resourceRequest";
import ResourceRequestSubmitModal from "./ResourceRequestSubmitModal.vue";
import ResourceRequestAproveModal from "./ResourceRequestAproveModal.vue";
import { status } from "@/definitions/ResourceRequest.json";

const context = require.context(
  "../../components/admin/resourceRequest/types",
  true,
  /\.vue$/
);

const types = context.keys().reduce((acc, key) => {
  const { manifest, default: component } = context(key);
  acc[manifest.id] = {
    ...manifest,
    component,
  };
  return acc;
}, {});

export default {
  name: "ResourceRequestManagement",
  metaInfo: {
    title: "Solicitação de Recurso",
  },
  components: {
    ResourceRequestSubmitModal,
    ResourceRequestAproveModal,
  },

  data() {
    return {
      id: null,
      types,
      loading: true,
      error: null,
      itens: false,
      party: null,
      activePanel: null,
      successSave: false,
      resourceRequest: null,
      statuses: status,
    };
  },
  methods: {
    async loadResourceRequest() {
      try {
        const { resourceRequest } = await RESOURCE_REQUEST.get(this.id);
        this.resourceRequest = resourceRequest;
        this.itens = resourceRequest.items.map((item) => {
          const oldItem =
            item.id && this.itens && this.itens.find((i) => i.id === item.id);
          return {
            ...item,
            valid: oldItem ? oldItem.valid : item.valid || false,
          };
        });
        this.party = resourceRequest.Party || null;
      } catch (e) {
        this.error = e.message || "Erro ao carregar solicitação de recurso";
      } finally {
        this.loading = false;
      }
    },
    async saveResourceRequest() {
      const { resourceRequest } = await RESOURCE_REQUEST.update(this.id, {
        items: this.itens,
      });
      return resourceRequest;
    },
    async deleteDraft() {
      this.loading = "delete";
      this.error = null;
      try {
        const confirm = window.confirm(
          "Tem certeza que deseja excluir o rascunho?"
        );
        if (!confirm) return;

        await RESOURCE_REQUEST.delete(this.id);
        this.$router.push("/admin/resource-request");
      } catch (e) {
        this.error = e.message || "Erro ao excluir rascunho";
      } finally {
        this.loading = false;
      }
    },
    async saveDraft() {
      if (this.successSave) return;
      this.loading = "draft";
      this.error = null;
      this.activePanel = null;
      try {
        await this.saveResourceRequest();
        await this.loadResourceRequest();
        this.successSave = true;
        setTimeout(() => {
          this.successSave = false;
        }, 3000);
      } catch (e) {
        this.error = e.message || "Erro ao salvar solicitação de recurso";
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      this.loading = "submit";
      this.error = null;
      try {
        await this.saveDraft();
        this.$emit("submit-resource-request");
      } catch (e) {
        this.error = e.message || "Erro ao enviar solicitação de recurso";
      } finally {
        this.loading = false;
      }
    },
    async toDraft() {
      this.loading = "toDraft";
      this.error = null;
      try {
        await RESOURCE_REQUEST.reject(this.id, { toDraft: true });
        await this.loadResourceRequest();
      } catch (e) {
        this.error = e.message || "Erro ao voltar para rascunho";
      } finally {
        this.loading = false;
      }
    },
    async reject() {
      this.loading = "reject";
      this.error = null;
      try {
        const reason = window.prompt("Digite o motivo da rejeição");
        await RESOURCE_REQUEST.reject(this.id, { reason });
        await this.loadResourceRequest();
      } catch (e) {
        this.error = e.message || "Erro ao rejeitar solicitação de recurso";
      } finally {
        this.loading = false;
      }
    },

    addItem(item) {
      let data = types[item].defaultData && types[item].defaultData();
      this.itens.push({
        type: item,
        valid: false,
        amount: 0,
        data: data || {},
      });
      this.activePanel = this.itens.length - 1;
    },
    updateItem(key, data) {
      this.itens[key].data = data;
    },
    updateValid(key, valid) {
      this.itens[key].valid = valid;
    },
    updateTotal(key, amount) {
      this.itens[key].amount = amount;
    },
  },
  computed: {
    ...mapGetters("company", ["selectedCompany"]),
    ...mapGetters("auth", ["hasPermission"]),
    total() {
      if (!this.itens) return 0;
      return this.itens.reduce((acc, item) => acc + item.amount || 0, 0);
    },
    isValid() {
      if (!this.itens || this.itens.length === 0) return false;
      return !this.itens.some((item) => !item.valid);
    },
  },
  watch: {
    selectedCompany: {
      handler() {
        this.$router.push({
          name: "admin.resource-request",
        });
      },
    },
  },
  async mounted() {
    this.id = this.$route.params.id;
    await this.loadResourceRequest();
  },
};
</script>
